// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Divider,
  Icon,
  Button,
  Avatar,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdSupportAgent, MdChat } from "react-icons/md";
import { formatDate } from "utils/format";
import supportService from "services/supportService";

export default function Support() {
  const navigate = useNavigate();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const cardHoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const subtextColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const emptyIconBg = useColorModeValue("gray.100", "whiteAlpha.100");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    const fetchConversations = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await supportService.getConversations();
        if (result.success && result.data) {
          setConversations(result.data);
        } else {
          setError(result.error || "Failed to load conversations");
        }
      } catch (err) {
        console.error("Error fetching conversations:", err);
        setError("Failed to load conversations. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, []);

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1000px" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" justify="space-between" flexWrap="wrap" gap="4">
          <Flex align="center" gap="3">
            <Flex
              w="12"
              h="12"
              borderRadius="xl"
              bg={iconBoxBg}
              align="center"
              justify="center"
            >
              <Icon as={MdSupportAgent} color="brand.500" boxSize="6" />
            </Flex>
            <Box>
              <Text color={textColor} fontSize="2xl" fontWeight="700">
                Support
              </Text>
              <Text color={textColorSecondary} fontSize="sm">
                {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
              </Text>
            </Box>
          </Flex>
        </Flex>

        <Card p="0" bg={cardBg} overflow="hidden" borderRadius="16px" boxShadow="sm">
          <Flex align="center" gap="3" px="6" py="4" borderBottomWidth="1px" borderColor={borderColor}>
            <Icon as={MdChat} color="brand.500" boxSize="5" />
            <Text color={textColor} fontSize="lg" fontWeight="600">
              Conversations
            </Text>
          </Flex>
          <Divider borderColor={borderColor} />

          {conversations && conversations.length > 0 ? (
            <Box py="2">
              {conversations.map((conv) => (
                <Flex
                  key={conv.id}
                  align="center"
                  gap="4"
                  px="6"
                  py="4"
                  _hover={{ bg: cardHoverBg }}
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                  _last={{ borderBottom: "none" }}
                >
                  <Avatar
                    size="md"
                    name={conv.email || "Visitor"}
                    bg="brand.400"
                    color="white"
                    flexShrink={0}
                  />
                  <Box flex="1" minW="0">
                    <Text color={textColor} fontSize="md" fontWeight="600" noOfLines={1}>
                      {conv.email || "—"}
                    </Text>
                    <Text color={subtextColor} fontSize="sm" noOfLines={1} mt="0.5">
                      {conv.last_message_at
                        ? `Last message ${formatDate(conv.last_message_at)}`
                        : `Started ${formatDate(conv.created_at)}`}
                    </Text>
                  </Box>
                  <Flex align="center" gap="3" flexShrink={0}>
                    <Text color={textColorSecondary} fontSize="xs" whiteSpace="nowrap">
                      {conv.last_message_at ? formatDate(conv.last_message_at) : formatDate(conv.created_at)}
                    </Text>
                    <Button
                      leftIcon={<Icon as={MdChat} />}
                      colorScheme="brand"
                      size="sm"
                      borderRadius="lg"
                      onClick={() => navigate(`/admin/support/conversation/${conv.id}`)}
                    >
                      Chat
                    </Button>
                  </Flex>
                </Flex>
              ))}
            </Box>
          ) : (
            <Flex direction="column" align="center" justify="center" py="16" px="6">
              <Flex
                w="16"
                h="16"
                borderRadius="full"
                bg={emptyIconBg}
                align="center"
                justify="center"
                mb="4"
              >
                <Icon as={MdChat} color={textColorSecondary} boxSize="8" />
              </Flex>
              <Text color={textColor} fontWeight="600" mb="1">
                No conversations yet
              </Text>
              <Text color={textColorSecondary} fontSize="sm" textAlign="center">
                When visitors start a chat, they will appear here.
              </Text>
            </Flex>
          )}
        </Card>
      </Flex>
    </Box>
  );
}
